import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../../../assets/images/logo_klhk.png';
import Ikon from './publik/Ikon';
import { MENU_INFORMASI } from './publik/topikPublik';

/*
 * Kepala halaman publik mengikuti desain Figma customer (Nov 2026): bilah
 * identitas hijau 100px lalu bilah menu putih 62px. Tombol Masuk/Daftar sengaja
 * TIDAK ada (disembunyikan di Figma); pintu masuk aplikasi lewat menu
 * Informasi > PILAR B3 dan tautan "Internal" di kaki halaman.
 */
const MENU = [
    { label: 'Beranda', ke: '/', persis: true },
    { label: 'Informasi', anak: MENU_INFORMASI },
    { label: 'Dokumen & Peraturan', ke: '/arsip' },
    { label: 'Berita & Artikel', ke: '/berita', juga: ['/artikel'] },
    { label: 'Galeri', ke: '/galeri' },
    { label: 'FAQ', ke: '/faq' },
];

function aktif(item, pathname) {
    if (item.anak) return pathname.startsWith('/informasi');
    if (item.persis) return pathname === item.ke;
    return [item.ke, ...(item.juga || [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/* Lambang dalam lencana bulat putih 56px. */
export function LencanaLogo({ className = 'size-14' }) {
    return (
        <span className={`inline-flex shrink-0 items-center justify-center rounded-full border border-pb-bingkai bg-white p-2 ${className}`}>
            <img src={logo} alt="" className="size-full object-contain" />
        </span>
    );
}

function MenuInformasi({ item, sedangAktif }) {
    const [buka, setBuka] = useState(false);
    const ref = useRef(null);
    const location = useLocation();

    useEffect(() => setBuka(false), [location.pathname]);

    useEffect(() => {
        if (!buka) return undefined;
        const tutup = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setBuka(false);
        };
        const esc = (e) => e.key === 'Escape' && setBuka(false);
        document.addEventListener('mousedown', tutup);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', tutup);
            document.removeEventListener('keydown', esc);
        };
    }, [buka]);

    return (
        <div ref={ref} className="relative flex h-full">
            <button
                type="button"
                aria-expanded={buka}
                aria-haspopup="true"
                onClick={() => setBuka((b) => !b)}
                className={`flex h-full items-center gap-1 border-b-[3px] font-body text-sm ${
                    sedangAktif || buka
                        ? 'border-pb-aksen font-semibold text-pb-aksen'
                        : 'border-transparent font-medium text-pb-ink hover:text-pb-aksen'
                }`}
            >
                {item.label}
                <Ikon nama="bawah" className={`size-5 transition-transform ${buka ? 'rotate-180' : ''}`} />
            </button>
            {buka && (
                <div className="absolute left-1/2 top-full z-50 mt-2 w-80 -translate-x-1/2 rounded-2xl border border-pb-bingkai bg-white p-3 shadow-[0_12px_32px_rgba(0,89,82,0.14)]">
                    {item.anak.map((anak) => (
                        <Link
                            key={anak.ke}
                            to={anak.ke}
                            className="flex items-center gap-3 rounded-lg px-4 py-3 font-body text-sm text-pb-ink hover:bg-pb-kabut"
                        >
                            <span className="inline-flex size-8 items-center justify-center rounded-full bg-pb-kabut text-pb-aksen">
                                <Ikon nama="info" className="size-5" />
                            </span>
                            {anak.label}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function HeaderLanding() {
    const { pathname } = useLocation();
    const [menuPonsel, setMenuPonsel] = useState(false);

    useEffect(() => setMenuPonsel(false), [pathname]);

    return (
        <header className="font-body">
            <div className="bg-g-600">
                <div className="mx-auto flex min-h-[72px] max-w-[1440px] items-center justify-between gap-4 px-4 py-3 sm:px-8 lg:min-h-[100px] lg:px-20">
                    <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="Direktorat Pengelolaan B3 - kembali ke beranda">
                        <LencanaLogo className="size-11 lg:size-14" />
                        <span className="min-w-0">
                            <span className="block text-base font-semibold leading-tight text-white lg:text-[21px]">
                                Direktorat Pengelolaan B3
                            </span>
                            <span className="mt-1 block text-[11px] leading-snug text-white lg:text-xs">
                                Kementerian Lingkungan Hidup / Badan Pengendalian Lingkungan Hidup
                            </span>
                        </span>
                    </Link>
                    <button
                        type="button"
                        className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-white hover:bg-g-700 lg:hidden"
                        aria-expanded={menuPonsel}
                        aria-controls="menu-ponsel"
                        aria-label={menuPonsel ? 'Tutup menu' : 'Buka menu'}
                        onClick={() => setMenuPonsel((b) => !b)}
                    >
                        <Ikon nama={menuPonsel ? 'tutup' : 'menu'} className="size-6" />
                    </button>
                </div>
            </div>

            {/* Menu desktop */}
            <nav aria-label="Menu utama" className="hidden border-y border-pb-garis-menu bg-white lg:block">
                <div className="mx-auto flex h-[62px] max-w-[1440px] items-stretch justify-between px-20">
                    {MENU.map((item) =>
                        item.anak ? (
                            <MenuInformasi key={item.label} item={item} sedangAktif={aktif(item, pathname)} />
                        ) : (
                            <NavLink
                                key={item.label}
                                to={item.ke}
                                className={`flex items-center border-b-[3px] text-sm ${
                                    aktif(item, pathname)
                                        ? 'border-pb-aksen font-semibold text-pb-aksen'
                                        : 'border-transparent font-medium text-pb-ink hover:text-pb-aksen'
                                }`}
                            >
                                {item.label}
                            </NavLink>
                        )
                    )}
                </div>
            </nav>

            {/* Menu ponsel & tablet */}
            {menuPonsel && (
                <nav id="menu-ponsel" aria-label="Menu utama" className="border-b border-pb-garis bg-white lg:hidden">
                    <ul className="mx-auto max-w-[1440px] px-4 py-2 sm:px-8">
                        {MENU.map((item) =>
                            item.anak ? (
                                <li key={item.label} className="border-b border-pb-garis py-2">
                                    <p className="px-2 py-2 text-xs font-semibold uppercase tracking-[1.5px] text-pb-aksen">
                                        {item.label}
                                    </p>
                                    <ul>
                                        {item.anak.map((anak) => (
                                            <li key={anak.ke}>
                                                <Link
                                                    to={anak.ke}
                                                    className="flex min-h-[44px] items-center gap-3 rounded-md px-2 text-sm text-pb-ink hover:bg-pb-kabut"
                                                >
                                                    <Ikon nama="info" className="size-5 text-pb-aksen" />
                                                    {anak.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ) : (
                                <li key={item.label} className="border-b border-pb-garis last:border-b-0">
                                    <NavLink
                                        to={item.ke}
                                        className={`flex min-h-[48px] items-center px-2 text-sm ${
                                            aktif(item, pathname) ? 'font-semibold text-pb-aksen' : 'font-medium text-pb-ink'
                                        }`}
                                    >
                                        {item.label}
                                    </NavLink>
                                </li>
                            )
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
